import { gql } from '@apollo/client';
import { Flex } from '@energiebespaarders/symbols';
import FaqBreadCrumbs from '~/components/FAQ/breadCrumbs/FaqBreadCrumbs';
import type { CategoryPageProps } from '~/components/FAQ/Category';
import Category from '~/components/FAQ/Category';
import type { ParentCategoryPageProps } from '~/components/FAQ/ParentCategory';
import ParentCategory from '~/components/FAQ/ParentCategory';
import SubNavbar from '~/components/FAQ/SubNavbar';
import type { TopicPageProps } from '~/components/FAQ/Topic';
import Topic from '~/components/FAQ/Topic';
import config from '~/config';
import type {
  fetchCategoryAndTopicBySlug,
  fetchCategoryAndTopicBySlugVariables,
  fetchCategoryAndTopicBySlug_allFaqCategories,
  fetchCategoryAndTopicBySlug_allFaqTopics,
} from '~/types/generated/cms/fetchCategoryAndTopicBySlug';
import type {
  getAllCategories,
  getAllCategories_allFaqCategories,
} from '~/types/generated/cms/getAllCategories';
import type { getAllTopics } from '~/types/generated/cms/getAllTopics';
import type { getCategories } from '~/types/generated/cms/getCategories';
import type {
  getFaqItemsByCategory,
  getFaqItemsByCategoryVariables,
} from '~/types/generated/cms/getFaqItemsByCategory';
import type {
  getFaqItemsbyTopicId,
  getFaqItemsbyTopicIdVariables,
} from '~/types/generated/cms/getFaqItemsbyTopicId';
import type {
  getSubCategories,
  getSubCategoriesVariables,
} from '~/types/generated/cms/getSubCategories';
import type {
  getTopicsByCategory,
  getTopicsByCategoryVariables,
} from '~/types/generated/cms/getTopicsByCategory';
import type { ErrorPageProps } from '~/types/next';
import {
  CategoryFragment,
  FaqItemFragment,
  TopicFieldsFragment,
} from 'src/datoCMS/fragments/fragments';
import { getDatoClient } from 'src/datoCMS/datoCmsClient';
import { GET_ALL_TOPICS, GET_PARENT_CATEGORIES, revalidateInterval } from '..';
import type { GetStaticPaths, GetStaticProps } from 'next/types';

export const GET_FAQ_ITEMS_BY_TOPIC_ID = gql`
  ${FaqItemFragment}
  query getFaqItemsbyTopicId($topicId: ItemId) {
    allFaqItems(filter: { topic: { eq: $topicId } }) {
      ...FaqItemFragment
    }
  }
`;

const GET_ALL_CATEGORIES = gql`
  query getAllCategories {
    allFaqCategories {
      id
      isParent
      slug
      parentCategory {
        id
      }
    }
  }
`;

/**
 * This is a bit hacky, Dato doesnt let you filter records by nested fields ie topic.category
 * so here we pass in an array of topic Ids that belong to the category that we want.
 * We also may need to refactor this to retrieve the faqItems differently in future if a parentCategory
 * has more than 100 FaqItems associated with it.
 */
// ! NOTE: The locale variable is just here to make a sure global type for it gets generated. We're not using it anywhere yet, but without it the global types file breaks.
const GET_FAQ_ITEMS_BY_CATEGORY = gql`
  ${FaqItemFragment}
  query getFaqItemsByCategory($topicIds: [ItemId], $locale: SiteLocale) {
    allFaqItems(filter: { topic: { in: $topicIds } }, first: 100, locale: $locale) {
      ...FaqItemFragment
    }
  }
`;

const FETCH_CATEGORY_AND_TOPIC_BY_SLUG = gql`
  ${TopicFieldsFragment}
  ${CategoryFragment}
  query fetchCategoryAndTopicBySlug($slug: String) {
    allFaqTopics(filter: { slug: { eq: $slug } }) {
      ...TopicFieldsFragment
    }
    allFaqCategories(filter: { slug: { eq: $slug } }) {
      ...CategoryFragment
    }
  }
`;

const GET_SUB_CATEGORIES = gql`
  ${CategoryFragment}
  query getSubCategories($categoryId: ItemId) {
    allFaqCategories(filter: { parentCategory: { eq: $categoryId } }) {
      ...CategoryFragment
    }
  }
`;

const GET_TOPICS_BY_CATEGORY = gql`
  ${TopicFieldsFragment}
  query getTopicsByCategory($categoryId: ItemId) {
    allFaqTopics(filter: { category: { eq: $categoryId } }) {
      ...TopicFieldsFragment
    }
  }
`;

const client = getDatoClient();

type PageProps = ParentCategoryPageProps | CategoryPageProps | TopicPageProps | ErrorPageProps;
type Props = CategoryPageProps | ParentCategoryPageProps | TopicPageProps;

interface ICategoryUrl {
  slug: string[];
  categoryId: any;
}

const getFaqItemsBelongingToCategory = async (topicIds: string[]) => {
  const { data } = await client.query<getFaqItemsByCategory, getFaqItemsByCategoryVariables>({
    query: GET_FAQ_ITEMS_BY_CATEGORY,
    variables: { topicIds },
  });

  return data.allFaqItems;
};

const FaqPage: React.FC<Props> = props => {
  return (
    <Flex flexWrap="wrap" width={1}>
      <SubNavbar categories={props.navbarCategories} />
      <FaqBreadCrumbs />

      {props.type === 'parentCategory' ? (
        <ParentCategory
          parentCategory={props.parentCategory}
          subCategories={props.subCategories}
          faqItems={props.faqItems}
        />
      ) : props.type === 'category' ? (
        <Category category={props.category} topics={props.topics} faqItems={props.faqItems} />
      ) : (
        <Topic topic={props.topic} faqItems={props.faqItems} />
      )}
    </Flex>
  );
};

export default FaqPage;

function constructTopicUrls(baseUrls: ICategoryUrl[], topics: getAllTopics) {
  return topics.allFaqTopics.map(topic => {
    const baseUrl = baseUrls.find(b => b.categoryId === topic.category!.id);

    return [...baseUrl!.slug!, topic.slug!];
  });
}

function constructCategoryUrl(
  category: getAllCategories_allFaqCategories,
  categories: getAllCategories,
): ICategoryUrl {
  const parentCategory = categories.allFaqCategories.find(
    pc => pc.id === category.parentCategory?.id,
  );

  if (parentCategory) {
    return {
      slug: [...constructCategoryUrl(parentCategory, categories).slug, category.slug!],
      categoryId: category.id,
    };
  }

  return {
    slug: [category.slug!],
    categoryId: category.id,
  };
}

const getParentCategoryProps = async (
  parentCategory: fetchCategoryAndTopicBySlug_allFaqCategories,
): Promise<Omit<ParentCategoryPageProps, 'navbarCategories'>> => {
  const { data: subCategories } = await client.query<getSubCategories, getSubCategoriesVariables>({
    query: GET_SUB_CATEGORIES,
    variables: { categoryId: parentCategory.id },
    fetchPolicy: 'network-only',
  });

  const { data: topics } = await client.query<getAllTopics>({
    query: GET_ALL_TOPICS,
    fetchPolicy: 'network-only',
  });

  const finalData = subCategories.allFaqCategories.map(c => {
    const topicsBelongingToSubCategory = topics.allFaqTopics.filter(t => {
      return t.category!.id === c.id;
    });

    return {
      category: c,
      topics: topicsBelongingToSubCategory,
    };
  });

  const faqItems = await getFaqItemsBelongingToCategory(
    topics.allFaqTopics
      .filter(t => t.category?.parentCategory?.id === parentCategory.id)
      .map(i => i.id),
  );

  return {
    type: 'parentCategory',
    parentCategory,
    subCategories: finalData,
    faqItems,
  };
};

const getCategoryProps = async (
  category: fetchCategoryAndTopicBySlug_allFaqCategories,
): Promise<Omit<CategoryPageProps, 'navbarCategories'>> => {
  const {
    data: { allFaqTopics: topics },
  } = await client.query<getTopicsByCategory, getTopicsByCategoryVariables>({
    query: GET_TOPICS_BY_CATEGORY,
    variables: { categoryId: category.id },
    fetchPolicy: 'network-only',
  });

  const faqItems = await getFaqItemsBelongingToCategory(topics.map(t => t.id));

  return {
    type: 'category',
    category,
    topics,
    faqItems,
  };
};

const getFaqItems = async (topic: fetchCategoryAndTopicBySlug_allFaqTopics) => {
  const { data: faqItems } = await client.query<
    getFaqItemsbyTopicId,
    getFaqItemsbyTopicIdVariables
  >({
    query: GET_FAQ_ITEMS_BY_TOPIC_ID,
    variables: { topicId: topic.id },
    fetchPolicy: 'network-only',
  });

  return faqItems.allFaqItems;
};

// This component displays either a parentCategory, category, or Topic page.
// 1 - makes a call to retrieve both a category and topic by slug
// 2 - depending on which is null it will either retrieve the props for a
//     Category / parentCategory or Topic page
// 3 - it returns the relevant props and passes them into the component
// 4 - it also returns navbarProps because the SubNavbar.tsx retrieves its info from the props passed into this component
//     this file uses NextJs Catch all routes:  https://nextjs.org/docs/routing/dynamic-routes#catch-all-routes

export const getStaticProps: GetStaticProps<PageProps> = async ({ params, preview }) => {
  const datoClient = getDatoClient(preview);

  const { data: categoryAndTopic } = await datoClient.query<
    fetchCategoryAndTopicBySlug,
    fetchCategoryAndTopicBySlugVariables
  >({
    query: FETCH_CATEGORY_AND_TOPIC_BY_SLUG,
    variables: { slug: params?.slugs ? params.slugs[params.slugs.length - 1] : null },
    fetchPolicy: 'network-only',
  });

  const { data: categories } = await datoClient.query<getCategories>({
    query: GET_PARENT_CATEGORIES,
    fetchPolicy: 'network-only',
  });

  const category = categoryAndTopic.allFaqCategories[0];
  const topic = categoryAndTopic.allFaqTopics[0];

  if (topic) {
    const faqItems = await getFaqItems(topic);

    return {
      props: {
        type: 'topic',
        topic: topic,
        faqItems: faqItems,
        navbarCategories: categories.allFaqCategories,
      },
      revalidate: revalidateInterval,
    };
  } else if (category) {
    const finalCategory = await (category.isParent
      ? getParentCategoryProps(category)
      : getCategoryProps(category));

    return {
      revalidate: revalidateInterval,
      props: { ...finalCategory, navbarCategories: categories.allFaqCategories },
    };
  } else {
    return {
      notFound: true,
    };
  }
};

export const constructFaqSlugs = async () => {
  const { data: categories } = await client.query<getAllCategories>({
    query: GET_ALL_CATEGORIES,
    fetchPolicy: 'network-only',
  });
  const { data: topics } = await client.query<getAllTopics>({
    query: GET_ALL_TOPICS,
    fetchPolicy: 'network-only',
  });

  const categoryUrls = categories.allFaqCategories.map(c => constructCategoryUrl(c, categories));

  const topicUrls = constructTopicUrls(categoryUrls, topics);

  return [...categoryUrls.map(url => url.slug), ...topicUrls];
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (config.build.skipStaticGeneration) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const completeUrls = await constructFaqSlugs();

  return {
    paths: completeUrls.map(item => {
      return {
        params: {
          slugs: item,
        },
      };
    }),
    fallback: 'blocking',
  };
};
